import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Tag, Plus, Trash2, Copy, Check, Gift, Users, Calendar,
  Hash, TrendingUp, Zap, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState("all"); // all, active, hooks, referral
  const [copied, setCopied] = useState(null);

  const [form, setForm] = useState({
    code: "",
    type: "percentage",
    value: 10,
    free_item_description: "",
    min_order: 0,
    max_uses: null,
    per_customer_limit: 1,
    allowed_emails: "",
    starts_at: "",
    expires_at: "",
    description: "",
  });

  const fetchCoupons = useCallback(async () => {
    try {
      const params = {};
      if (filter === "active") params.active_only = true;
      if (filter === "hooks") params.hook_type = "first_order_hook";
      if (filter === "referral") params.hook_type = "referral";
      const { data } = await axios.get(`${API}/coupons`, { params, withCredentials: true });
      let filtered = data.coupons || [];
      if (filter === "hooks") {
        const { data: d2 } = await axios.get(`${API}/coupons`, { params: { hook_type: "second_order_hook" }, withCredentials: true });
        filtered = [...filtered, ...(d2.coupons || [])];
      }
      setCoupons(filtered);
    } catch (err) {
      console.error("Failed to load coupons", err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/coupons/stats`, { withCredentials: true });
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => { fetchCoupons(); fetchStats(); }, [fetchCoupons, fetchStats]);

  const handleCreate = async () => {
    try {
      const payload = {
        ...form,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        per_customer_limit: parseInt(form.per_customer_limit) || 1,
        min_order: parseFloat(form.min_order) || 0,
        value: parseFloat(form.value) || 0,
        allowed_emails: form.allowed_emails ? form.allowed_emails.split(",").map(e => e.trim()).filter(Boolean) : [],
        starts_at: form.starts_at || null,
        expires_at: form.expires_at || null,
      };
      await axios.post(`${API}/coupons`, payload, { withCredentials: true });
      setShowCreate(false);
      setForm({ code: "", type: "percentage", value: 10, free_item_description: "", min_order: 0, max_uses: null, per_customer_limit: 1, allowed_emails: "", starts_at: "", expires_at: "", description: "" });
      fetchCoupons();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create coupon");
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this coupon?")) return;
    try {
      await axios.delete(`${API}/coupons/${id}`, { withCredentials: true });
      fetchCoupons();
      fetchStats();
    } catch {}
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const typeLabel = (c) => {
    if (c.type === "percentage") return `${c.value}% off`;
    if (c.type === "flat") return `$${c.value} off`;
    if (c.type === "free_item") return c.free_item_description || "Free item";
    return c.type;
  };

  const hookLabel = (h) => {
    if (h === "first_order_hook") return "1st Order Hook";
    if (h === "second_order_hook") return "2nd Order Hook";
    if (h === "referral") return "Referral";
    return "";
  };

  return (
    <div className="space-y-4" data-testid="coupon-management">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard label="Total Coupons" value={stats.total_coupons} icon={Tag} color="blue" />
          <StatCard label="Active" value={stats.active_coupons} icon={Zap} color="green" />
          <StatCard label="Redemptions" value={stats.total_redemptions} icon={TrendingUp} color="purple" />
          <StatCard label="Loyalty Hooks" value={(stats.loyalty_hooks?.first_order || 0) + (stats.loyalty_hooks?.second_order || 0)} icon={Gift} color="amber" />
          <StatCard label="Referral Codes" value={stats.referral_coupons} icon={Users} color="orange" />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {["all", "active", "hooks", "referral"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${filter === f ? "bg-orange-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}
              data-testid={`filter-${f}`}
            >
              {f === "all" ? "All" : f === "active" ? "Active" : f === "hooks" ? "Loyalty Hooks" : "Referrals"}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)} className="bg-orange-600 hover:bg-orange-700" data-testid="create-coupon-btn">
          <Plus className="w-4 h-4 mr-1" /> New Coupon
        </Button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader><CardTitle className="text-white text-sm">Create Coupon</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Code (blank = auto)</Label>
                <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="SUMMER25" className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-code" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Type</Label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="mt-1 w-full h-9 px-2 text-sm rounded-md bg-zinc-800 border border-zinc-700 text-white" data-testid="form-type">
                  <option value="percentage">Percentage Off</option>
                  <option value="flat">Flat $ Off</option>
                  <option value="free_item">Free Item</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">{form.type === "free_item" ? "Item Name" : form.type === "percentage" ? "% Off" : "$ Off"}</Label>
                {form.type === "free_item" ? (
                  <Input value={form.free_item_description} onChange={e => setForm(f => ({ ...f, free_item_description: e.target.value }))} placeholder="Free Hat" className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-free-item" />
                ) : (
                  <Input type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-value" />
                )}
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Min Order ($)</Label>
                <Input type="number" value={form.min_order} onChange={e => setForm(f => ({ ...f, min_order: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-min-order" />
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Max Total Uses (blank = unlimited)</Label>
                <Input type="number" value={form.max_uses || ""} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} placeholder="Unlimited" className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-max-uses" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Per Customer Limit</Label>
                <Input type="number" value={form.per_customer_limit} onChange={e => setForm(f => ({ ...f, per_customer_limit: e.target.value }))} className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-per-customer" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Start Date</Label>
                <Input type="datetime-local" value={form.starts_at} onChange={e => setForm(f => ({ ...f, starts_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))} className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-starts" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Expiry Date</Label>
                <Input type="datetime-local" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))} className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-expires" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Allowed Emails (comma-separated, blank = anyone)</Label>
                <Input value={form.allowed_emails} onChange={e => setForm(f => ({ ...f, allowed_emails: e.target.value }))} placeholder="vip@company.com, boss@corp.com" className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-emails" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Description</Label>
                <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Summer sale 2026" className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="form-description" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)} className="border-zinc-700 text-zinc-400">Cancel</Button>
              <Button size="sm" onClick={handleCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-coupon-btn">Create Coupon</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coupon List */}
      {loading ? (
        <div className="text-center py-8 text-zinc-500">Loading coupons...</div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-12">
          <Tag className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">No coupons yet. Create your first promo code!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {coupons.map(c => (
            <Card key={c.id} className={`bg-zinc-900 border-zinc-800 ${!c.active ? 'opacity-50' : ''}`} data-testid={`coupon-${c.code}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => copyCode(c.code)} className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded-md transition" data-testid={`copy-${c.code}`}>
                      <span className="text-sm font-mono font-bold text-orange-400 tracking-wider">{c.code}</span>
                      {copied === c.code ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-zinc-500" />}
                    </button>
                    <div>
                      <span className="text-sm font-medium text-white">{typeLabel(c)}</span>
                      {c.description && <span className="text-xs text-zinc-500 ml-2">{c.description}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.hook_type && (
                      <Badge className={`text-[10px] ${c.hook_type.includes("first") ? "bg-amber-900/50 text-amber-400" : c.hook_type.includes("second") ? "bg-purple-900/50 text-purple-400" : "bg-blue-900/50 text-blue-400"}`}>
                        {hookLabel(c.hook_type)}
                      </Badge>
                    )}
                    {c.auto_generated && <Badge className="text-[10px] bg-zinc-800 text-zinc-400">Auto</Badge>}
                    <Badge className={`text-[10px] ${c.active ? "bg-green-900/50 text-green-400" : "bg-red-900/50 text-red-400"}`}>
                      {c.active ? "Active" : "Inactive"}
                    </Badge>
                    <span className="text-xs text-zinc-500">
                      <Hash className="w-3 h-3 inline mr-0.5" />{c.used_count || 0}{c.max_uses ? `/${c.max_uses}` : ""} used
                    </span>
                    {c.allowed_emails?.length > 0 && (
                      <span className="text-xs text-zinc-500"><Users className="w-3 h-3 inline mr-0.5" />{c.allowed_emails.length}</span>
                    )}
                    {c.expires_at && (
                      <span className="text-xs text-zinc-500"><Calendar className="w-3 h-3 inline mr-0.5" />{new Date(c.expires_at).toLocaleDateString()}</span>
                    )}
                    {c.active && (
                      <Button size="sm" variant="ghost" onClick={() => deactivate(c.id)} className="text-red-400 hover:text-red-300 h-7 w-7 p-0" data-testid={`deactivate-${c.code}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card className="bg-zinc-900 border-zinc-800">
    <CardContent className="p-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${color}-900/30`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      <div>
        <p className="text-lg font-bold text-white">{value}</p>
        <p className="text-[10px] text-zinc-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default CouponManagement;
